import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface DownloadNoticeContextValue {
  show: (title: string) => void;
}

const DownloadNoticeContext = createContext<DownloadNoticeContextValue>({ show: () => {} });

export const useDownloadNotice = () => useContext(DownloadNoticeContext);

export default function DownloadNoticeProvider({ children }: { children: ReactNode }) {
  const [title, setTitle] = useState<string | null>(null);

  const show = useCallback((value: string) => setTitle(value), []);
  const close = useCallback(() => setTitle(null), []);

  const value = useMemo(() => ({ show }), [show]);

  return (
    <DownloadNoticeContext.Provider value={value}>
      {children}
      {title !== null && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <button
            type="button"
            aria-label="关闭下载提示"
            className="absolute inset-0 h-full w-full cursor-default bg-foreground-950/50 animate-fade-in"
            onClick={close}
          />
          <div className="relative z-10 w-full max-w-md animate-fade-up rounded-lg border border-background-200 bg-background-50 p-6 md:p-7">
            <div className="flex items-start gap-4">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-download-2-line text-2xl" />
              </span>
              <div className="min-w-0 flex-1">
                <h3 className="text-base font-bold text-foreground-950">下载演示说明</h3>
                <p className="mt-1 break-words text-sm text-foreground-600">
                  {title}
                </p>
              </div>
            </div>

            <p className="mt-4 rounded-md bg-background-100 p-3 text-xs leading-relaxed text-foreground-600">
              本页面为前端交互演示，<strong className="font-semibold text-foreground-800">真实下载功能待接入</strong>。
              版权内容请支持正版渠道获取，本站不提供任何实际文件下载。
            </p>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={close}
                className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-check-line" />
                继续浏览
              </button>
            </div>
          </div>
        </div>
      )}
    </DownloadNoticeContext.Provider>
  );
}